import {api, config} from '@bitstillery/common/app'
import type {PublicCmsMediaFolderResponse, PublicCmsMediaFolderTreeResponse} from '@bitstillery/common/api/fact2server'
import type {ApiResult} from '@bitstillery/common/lib/api'
import {cached} from '@bitstillery/common/lib/api_cache'

export interface CmsMediaFolderFile {
    name: string
    uri: string
}

export interface CmsMediaSubfolder {
    files: CmsMediaFolderFile[]
    name: string
}

/**
 * Which library a `path` is relative to: the visiting account's own tree, or the shared one that
 * belongs to no account (brand owner photography and anything else that is the same picture whichever
 * portal renders it).
 */
export type CmsMediaLibrary = 'account' | 'shared'

/**
 * Files in a public CMS media library folder (e.g. `card/placeholder`, `fun-fact`) — used by
 * components that don't know the file names in advance. Public (shared across requests) and cached
 * with a short TTL via `cached()`, which also de-dupes concurrent callers, carries the resolved list
 * across the SSR boundary (so the client's first render matches), and can be cleared by name
 * (`cms/media-folder`). Failures cache `[]` for the TTL and then self-heal.
 */
const media_folder = cached(
    'cms/media-folder',
    async (path: string, library: CmsMediaLibrary = 'account'): Promise<CmsMediaFolderFile[]> => {
        try {
            const {result, success}: ApiResult<Partial<PublicCmsMediaFolderResponse> | undefined> = await api.get(
                'portal/cms/media-folder',
                {path, library},
            )
            if (!success) return []
            // A 204 carries no body at all.
            return result?.files ?? []
        } catch {
            return []
        }
    },
    {ttl: 5 * 60_000, scope: 'public', key: (path, library = 'account') => `${library}:${path}`},
)

/**
 * Every subfolder of one public folder, with the images in each — one request for a set of folders
 * instead of one per folder (a tile per brand owner, each drawing from `brands/<slug>/`).
 */
const media_folder_tree = cached(
    'cms/media-folder-tree',
    async (path: string, library: CmsMediaLibrary = 'account'): Promise<CmsMediaSubfolder[]> => {
        try {
            const {result, success}: ApiResult<Partial<PublicCmsMediaFolderTreeResponse> | undefined> = await api.get(
                'portal/cms/media-folder-tree',
                {path, library},
            )
            if (!success) return []
            // A 204 carries no body at all.
            return result?.folders ?? []
        } catch {
            return []
        }
    },
    {ttl: 5 * 60_000, scope: 'public', key: (path, library = 'account') => `${library}:${path}`},
)

/** Files in a public CMS media library folder — fetched once per `path` (cached, deduped, TTL). */
export function load_media_folder_files(path: string, library: CmsMediaLibrary = 'account'): Promise<CmsMediaFolderFile[]> {
    return media_folder(path, library)
}

/** Synchronous read of the cached files for `path` (empty until loaded) — for warm SSR/first render. */
export function cached_media_folder_files(path: string, library: CmsMediaLibrary = 'account'): CmsMediaFolderFile[] {
    return media_folder.peek(path, library) ?? []
}

/** Subfolders of `path` with their images — fetched once per path (cached, deduped, TTL). */
export function load_media_folder_tree(path: string, library: CmsMediaLibrary = 'account'): Promise<CmsMediaSubfolder[]> {
    return media_folder_tree(path, library)
}

/** Synchronous read of the cached subfolders of `path` (empty until loaded) — for warm SSR/first render. */
export function cached_media_folder_tree(path: string, library: CmsMediaLibrary = 'account'): CmsMediaSubfolder[] {
    return media_folder_tree.peek(path, library) ?? []
}

/** Full image URL for a file returned by `load_media_folder_files`/`cached_media_folder_files`. */
export function media_folder_file_src(file: CmsMediaFolderFile): string {
    return `${config.media_photo_host}/${file.uri}`
}

/**
 * One file out of a folder, picked by `seed` and today's date.
 *
 * "Random" is what this looks like and not what it is, deliberately: the server renders the page and
 * the browser renders it again on hydration, so a `Math.random()` here would paint one image and then
 * swap it for another a moment later. Seeding on the caller's identity plus the UTC date keeps both
 * renders on the same file while still giving each tile a different one, and rotates the whole set
 * once a day. (A page rendered across midnight UTC swaps once — the one case the seed cannot cover.)
 */
export function pick_daily_file(files: CmsMediaFolderFile[], seed: string): CmsMediaFolderFile | null {
    if (files.length === 0) {
        return null
    }
    const today = new Date().toISOString().slice(0, 10)
    let hash = 0
    for (const char of `${seed}:${today}`) {
        hash = (hash * 31 + char.charCodeAt(0)) | 0
    }
    return files[Math.abs(hash) % files.length] ?? null
}
